///////////////////////////////////////////////////////////////////////////////////////
//                          Copyright (c) 2020 - 2024
//                           Coyote Logistics L.L.C.
//                         All Rights Reserved Worldwide
//
//WARNING:  This program (or document) is unpublished, proprietary
//property of Coyote Logistics L.L.C. and is to be maintained in strict confidence.
//Unauthorized reproduction, distribution or disclosure of this program
//(or document), or any program (or document) derived from it is
//prohibited by State and Federal law, and by local law outside of the U.S.
///////////////////////////////////////////////////////////////////////////////////////

import React, { Fragment, useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";

import Alert from "../../Alert";
import Modal from "../../Modal";
import { fileStatus, httpResponseStatus } from "../../../helpers/types";
import FileIcon from "../../form-controls/FileIcon";
import CloseIcon from "../../icons/CloseIcon";
import ErrorIcon from "../../icons/ErrorIcon";
import Spinner from "../../icons/Spinner";
import { postAttachment, postAttachmentV2 } from "../../../api/attachments";
import { CancelToken } from "../../../api/axios";
import {
  addDocumentsToUpload,
  removeDocumentToUpload,
  documentUploadEvent,
  fetchBidAttachments,
  setAllDocumentsUploaded
} from "../../../slices/bidSummarySlice";
import FileDropper from "../../FileDropper";
import DuplicateDocumentModal from "../DuplicateDocumentModal";
import SystemErrorModal from "../../SystemErrorModal";
import messages from "../../../helpers/messages";

export const DocumentUploadModal = ({
  handleClose,
  bidId,
  alertTitle,
  alertMessage,
  modalTitle,
  modalContent,
  buttonText,
  buttonStyle,
  checkboxContent,
  onSuccess,
  isLoading,
  isSuccess,
  isError,
  alertModalLarge,
  alertModalStyle,
  showAlertModal,
  isAwardUpload = false
}) => {
  const {
    bidSummary: { documents, allDocumentsUploaded },
    featureFlags: { useAttachmentV2Endpoint }
  } = useSelector(state => state);

  const dispatch = useDispatch();

  const [files, setFiles] = useState([]);
  const [cancelTokens, setCancelTokens] = useState([]);
  const [isChecked, setIsChecked] = useState(false);
  const [submitBy, setSubmitBy] = useState({ checked: false, document: false });

  useEffect(() => {
    if (allDocumentsUploaded && onSuccess) {
      onSuccess();
      dispatch(setAllDocumentsUploaded(false));
    }
  }, [allDocumentsUploaded, onSuccess, dispatch]);

  const uploadDocument = (documentId, tokens, replace = false) => {
    let attachmentAPI = useAttachmentV2Endpoint
      ? postAttachmentV2
      : postAttachment;
    attachmentAPI(bidId, replace, [files.find(f => f.id === documentId).file], {
      onUploadProgress: ({ loaded, total }) =>
        dispatch(
          documentUploadEvent({
            id: documentId,
            status: fileStatus.inProgress,
            complete: Math.floor((loaded / total) * 100)
          })
        ),
      cancelToken: new CancelToken(function executor(cancelToken) {
        tokens.push({ documentId, cancelToken });
      })
    }, isAwardUpload)
      .then(() =>
        dispatch(
          documentUploadEvent({
            id: documentId,
            status: fileStatus.uploaded,
            complete: 100
          })
        )
      )
      .catch(({ response }) => {
        dispatch(
          documentUploadEvent({
            id: documentId,
            status:
              response && response.status === httpResponseStatus.conflict
                ? fileStatus.conflict
                : fileStatus.error,
            complete: 0,
            conflictingFile:
              response && response.status === httpResponseStatus.conflict
                ? response.data.documents[0]
                : undefined
          })
        );
      });
  };

  const handleUpload = () => {
    const tokens = [];
    if (isChecked) {
      setSubmitBy({ checkbox: true, document: false });
      onSuccess();
    } else {
      setSubmitBy({ checkbox: false, document: true });
      documents.forEach(({ id, status }) => {
        status === fileStatus.notUploaded && uploadDocument(id, tokens);
      });
      setCancelTokens([...cancelTokens, ...tokens]);
    }
  };

  const handleRemoveFile = id => {
    const cancelToken = cancelTokens.find(token => token.id === id);

    cancelToken && cancelToken.cancelToken && cancelToken.cancelToken();
    setCancelTokens(
      cancelTokens.filter(token => token.id === id && { ...token })
    );

    setFiles(files.filter(file => file.id !== id && { ...file }));
    dispatch(removeDocumentToUpload({ id }));
  };

  const handleDrop = selectedFiles => {
    let newFiles = [];
    selectedFiles.forEach(file => {
      newFiles.push({
        file,
        name: file.path,
        id: Math.floor(Math.random() * Math.pow(10, 5))
      });
    });
    setFiles(newFiles.concat(files));
    dispatch(
      addDocumentsToUpload(
        newFiles.map(({ file, name, id }) => ({
          name,
          id,
          status: fileStatus.notUploaded,
          type: file.type
        }))
      )
    );
  };

  const handleModalClose = () => {
    dispatch(removeDocumentToUpload({ removeAll: true }));
    submitBy.document === true && dispatch(fetchBidAttachments(bidId));
    handleClose(submitBy.document === true || submitBy.checkbox === true);
  };

  const replaceDocument = documentId => {
    const tokens = [];
    uploadDocument(documentId, tokens, true);
    setCancelTokens([...cancelTokens, ...tokens]);
  };

  const completeDialog =
    (documents.length !== 0 &&
      documents.every(d => d.status === fileStatus.uploaded) &&
      isSuccess) ||
    (isChecked && isSuccess);

  const duplicateDocuments = documents.filter(
    d => d.status === fileStatus.conflict
  );

  return (
    <Fragment>
      {duplicateDocuments.length > 0 && (
        <DuplicateDocumentModal
          handleCancel={() => handleRemoveFile(duplicateDocuments[0].id)}
          handleReplace={() => replaceDocument(duplicateDocuments[0].id)}
          document={duplicateDocuments[0].conflictingFile}
        />
      )}
      {isError && (
        <SystemErrorModal
          open={isError}
          closeModal={handleModalClose}
          handleExit={handleModalClose}
          handleTryAgain={onSuccess}
        />
      )}
      <Alert
        title={alertTitle}
        message={alertMessage}
        open={showAlertModal && completeDialog}
        handleClose={handleModalClose}
        large={alertModalLarge}
        modalStyle={alertModalStyle}
      />
      <Modal
        title={modalTitle}
        open={!completeDialog && !duplicateDocuments.length && !isError}
        handleClose={handleModalClose}
        titleClass="modal__title_light uppercase"
        closeButtonClass="modal__close-button mr-16 mt-16"
        bodyClass="border-top-grey pl-24 pr-24 pt-16 pb-32"
        large={true}
      >
        {modalContent && (
          <span
            className="font-size-16 text-dim-grey"
            style={{ lineHeight: "18px" }}
          >
            {modalContent}
          </span>
        )}
        <FileDropper
          boxClassName="py-36 mt-12"
          handleDrop={handleDrop}
          data-testid="document-upload-modal"
          disabled={isChecked}
        >
          <span className="zeta">
            {messages.uploadYourFilesHereOr.defaultMessage}
          </span>
          <button id="choose-file" className="underline no-focus">
            {messages.browse.defaultMessage}
          </button>
        </FileDropper>

        <div className={"selected-files " + (documents.length > 0 && "pt-12")}>
          {documents.map(document => (
            <div
              data-testid="selected-file"
              className={
                "h-48 pt-8 pb-8 pos-relative progress-bar" +
                (documents.length === 1 ? "" : " border-bottom-grey")
              }
              key={document.id}
            >
              <FileIcon
                className="mr-12 mt-6"
                mimeType={document.type}
                width="18px"
                height="22px"
                fileExtension={document.name.split(".").pop()}
              />

              <div
                className={
                  "inline eight-tenths pos-absolute " +
                  (document.status === fileStatus.notUploaded ? "mt-8" : "mt-4")
                }
              >
                <p
                  data-testid="document-name"
                  className="truncate font-bold mb-0"
                  title={document.name}
                >
                  {document.name}
                </p>
                {(document.status === fileStatus.inProgress ||
                  document.status === fileStatus.uploaded) && (
                  <div className="file-progress-bar">
                    <div
                      style={{ width: (document.complete || "0") + "%" }}
                      className="file-progress-complete"
                    >
                      &nbsp;
                    </div>
                  </div>
                )}

                {document.status === fileStatus.error && (
                  <div style={{ color: "#B00020" }} className="font-size-10">
                    <ErrorIcon
                      className="mr-6"
                      height="10px"
                      viewBox="0 0 38 35"
                      fill="#B00020"
                    />
                    {messages.documentUploadErrorMessage.defaultMessage}
                  </div>
                )}
              </div>

              <button
                data-testid="remove-file"
                className="fr"
                onClick={() => handleRemoveFile(document.id)}
              >
                <CloseIcon
                  fill="#343434"
                  width="16px"
                  height="16px"
                  title=""
                  className="close-icon align-middle mt-8"
                />
              </button>
            </div>
          ))}
        </div>

        {checkboxContent && (
          <div className="mt-12">
            <input
              type="checkbox"
              className="mt-0 cursor-pointer"
              checked={isChecked}
              onChange={e => setIsChecked(e.target.checked)}
              disabled={documents.length}
            />
            <p className="mb-0 text-dim-grey inline-block pl-4 align-top">
              {checkboxContent}
            </p>
          </div>
        )}

        <div className="mt-24 mb-16">
          &nbsp;
          {isLoading ? (
            <button
              className="primary-button fr"
              data-testid="spinner-button"
              style={buttonStyle}
            >
              <Spinner
                className="spinner pos-relative "
                height="16px"
                width="16px"
                fill="#ffffff"
              />
            </button>
          ) : (
            <button
              data-testid="upload-documents-button"
              disabled={
                checkboxContent && isChecked
                  ? false
                  : documents.length === 0 ||
                    documents.findIndex(
                      d => d.status === fileStatus.notUploaded
                    ) === -1
              }
              onClick={handleUpload}
              className="primary-button fr univers-67-bold-cond"
              style={buttonStyle}
            >
              {buttonText}
            </button>
          )}
        </div>
      </Modal>
    </Fragment>
  );
};

DocumentUploadModal.defaultProps = {
  alertTitle: messages.uploadComplete.defaultMessage,
  alertMessage: messages.documentUploadSuccessMessage.defaultMessage,
  modalTitle: messages.documentUpload.defaultMessage,
  modalContent: "",
  buttonText: messages.upload.defaultMessage,
  isLoading: false,
  isSuccess: true,
  isError: false,
  showAlertModal: true
};

export default DocumentUploadModal;
